import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Pencil, Plus, Trash2, Wallet } from 'lucide-react';
import { DateTime } from 'luxon';
import type { BankAccountDto, CompensationDto } from '@workforce/shared';
import { api, ApiError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { fmtDate } from '@/lib/format';
import { Badge, Button, Card, ConfirmDialog, DescList, EmptyState, ErrorBanner, Field, Input, Modal, Spinner, Toggle } from './ui';

export function money(n: number | null | undefined, currency?: string, digits = 0): string {
  if (n == null || Number.isNaN(n)) return '—';
  const s = n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: Math.max(digits, n % 1 ? 2 : 0) });
  return currency ? `${s} ${currency}` : s;
}

export function describeSalary(c: Pick<CompensationDto, 'salaryAmount' | 'salaryCurrency' | 'payCurrency' | 'fixedRate'>): string {
  if (c.salaryCurrency === c.payCurrency) return money(c.salaryAmount, c.salaryCurrency);
  if (c.fixedRate) return t('{salary} at a fixed rate = {paid}', { salary: money(c.salaryAmount, c.salaryCurrency), paid: money(Math.round(c.salaryAmount * c.fixedRate), c.payCurrency) });
  return t("{salary}, paid in {currency} at each month's rate", { salary: money(c.salaryAmount, c.salaryCurrency), currency: c.payCurrency });
}

export function SalaryTab({ employeeId, employeeName }: { employeeId: string; employeeName: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['compensation', employeeId],
    queryFn: () => api.get<CompensationDto[]>(`/payroll/employees/${employeeId}/compensation`),
  });
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<CompensationDto | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/payroll/employees/${employeeId}/compensation/${id}`),
    onSuccess: () => {
      setRemoving(null);
      qc.invalidateQueries({ queryKey: ['compensation', employeeId] });
    },
  });
  if (q.isLoading) return <Spinner />;
  const list = q.data ?? [];
  const current = list.find((c) => c.isCurrent);
  return (
    <div className="space-y-6">
      <ErrorBanner error={q.error} />
      <Card
        title={t('Salary')}
        actions={
          <Button size="sm" icon={<Plus className="h-3.5 w-3.5" />} onClick={() => setAdding(true)}>
            {current ? t('Change salary') : t('Set salary')}
          </Button>
        }
      >
        {current ? (
          <div className="flex items-start gap-3">
            <span className="rounded-lg bg-emerald-50 p-2 text-emerald-700">
              <Wallet className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xl font-semibold text-slate-900">{describeSalary(current)}</p>
              <p className="text-sm text-slate-500">
                {t('Monthly · since {date}', { date: fmtDate(current.effectiveFrom) })} · {current.overtimeEligible ? t('overtime paid') : t('no overtime')}
                {current.fixedRate ? ` · ${t('fixed rate {rate}', { rate: `${current.fixedRate.toFixed(4)} ${current.payCurrency}/${current.salaryCurrency}` })}` : ''}
              </p>
            </div>
          </div>
        ) : (
          <EmptyState title={t('No salary set')} description={t("Set {name}'s monthly salary to include them in payroll.", { name: employeeName })} />
        )}
      </Card>
      <BankCard employeeId={employeeId} employeeName={employeeName} />
      {list.length > 0 && (
        <Card title={t('History')} bodyClass="p-0">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2">{t('From')}</th>
                <th className="px-4 py-2">{t('Salary')}</th>
                <th className="hidden px-4 py-2 md:table-cell">{t('Note')}</th>
                <th className="hidden px-4 py-2 sm:table-cell">{t('By')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="whitespace-nowrap px-4 py-2">
                    {fmtDate(c.effectiveFrom)} {c.isCurrent && <Badge tone="green">{t('Current')}</Badge>} {c.isFuture && <Badge tone="violet">{t('Upcoming')}</Badge>}
                  </td>
                  <td className="px-4 py-2">{describeSalary(c)}</td>
                  <td className="hidden px-4 py-2 text-slate-500 md:table-cell">{c.note ?? '—'}</td>
                  <td className="hidden px-4 py-2 text-slate-500 sm:table-cell">{c.createdBy ?? '—'}</td>
                  <td className="px-4 py-2 text-end">
                    {c.isFuture && (
                      <Button size="sm" variant="ghost" icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => setRemoving(c)} aria-label={t('Remove upcoming salary change')} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-3 text-xs text-slate-500">{t('Salary records are never edited: a change is a new line with the date it applies from. Only upcoming changes can be removed.')}</p>
        </Card>
      )}
      {adding && <SalaryModal employeeId={employeeId} current={current ?? null} onClose={() => setAdding(false)} />}
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
        title={t('Remove upcoming salary change?')}
        message={removing ? t('{salary} from {date}.', { salary: describeSalary(removing), date: fmtDate(removing.effectiveFrom) }) : ''}
        confirmLabel={t('Remove')}
        danger
        loading={remove.isPending}
        error={remove.error}
      />
    </div>
  );
}

function SalaryModal({ employeeId, current, onClose }: { employeeId: string; current: CompensationDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [f, setF] = useState({
    effectiveFrom: current ? DateTime.now().plus({ months: 1 }).startOf('month').toISODate()! : DateTime.now().toISODate()!,
    salaryAmount: current ? String(current.salaryAmount) : '',
    salaryCurrency: current?.salaryCurrency ?? user?.defaultPayCurrency ?? 'EGP',
    payCurrency: current?.payCurrency ?? user?.defaultPayCurrency ?? 'EGP',
    fixed: !!current?.fixedRate,
    fixedMode: 'amount' as 'amount' | 'rate',
    fixedRate: current?.fixedRate ? String(current.fixedRate) : '',
    fixedAmount: current?.fixedRate ? String(Math.round(current.salaryAmount * current.fixedRate)) : '',
    overtimeEligible: current?.overtimeEligible ?? true,
    note: '',
  });
  const amount = Number(f.salaryAmount);
  const differs = f.salaryCurrency.trim().toUpperCase() !== f.payCurrency.trim().toUpperCase();
  const rate = f.fixedMode === 'rate' ? Number(f.fixedRate) : amount > 0 ? Number(f.fixedAmount) / amount : NaN;
  const save = useMutation({
    mutationFn: () =>
      api.post(`/payroll/employees/${employeeId}/compensation`, {
        effectiveFrom: f.effectiveFrom,
        salaryAmount: amount,
        salaryCurrency: f.salaryCurrency,
        payCurrency: f.payCurrency,
        fixedRate: differs && f.fixed ? Number(rate.toFixed(8)) : null,
        overtimeEligible: f.overtimeEligible,
        note: f.note || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compensation', employeeId] });
      qc.invalidateQueries({ queryKey: ['payroll'] });
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={current ? t('Change salary') : t('Set salary')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('Cancel')}</Button>
          <Button loading={save.isPending} disabled={!(amount > 0) || (differs && f.fixed && !(rate > 0))} onClick={() => save.mutate()}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <ErrorBanner error={save.error} />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Monthly salary')}>
            <Input type="number" min="0" step="0.01" value={f.salaryAmount} onChange={(e) => setF({ ...f, salaryAmount: e.target.value })} />
          </Field>
          <Field label={t('Currency')} hint={t('e.g. {code}', { code: 'SAR' })}>
            <Input value={f.salaryCurrency} maxLength={3} onChange={(e) => setF({ ...f, salaryCurrency: e.target.value.toUpperCase() })} />
          </Field>
          <Field label={t('Paid in')} hint={t('e.g. {code}', { code: 'EGP' })}>
            <Input value={f.payCurrency} maxLength={3} onChange={(e) => setF({ ...f, payCurrency: e.target.value.toUpperCase() })} />
          </Field>
          <Field label={t('Applies from')}>
            <Input type="date" value={f.effectiveFrom} onChange={(e) => setF({ ...f, effectiveFrom: e.target.value })} />
          </Field>
        </div>
        {differs && (
          <div className="rounded-lg border border-slate-200 p-3">
            <Toggle
              checked={f.fixed}
              onChange={(v) => setF({ ...f, fixed: v })}
              label={t('Fixed exchange rate')}
              description={f.fixed ? t('The same amount every month.') : t("Converted at each month's {from} → {to} rate set on the Payroll page.", { from: f.salaryCurrency, to: f.payCurrency })}
            />
            {f.fixed && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label={t('Fixed amount ({currency})', { currency: f.payCurrency })}>
                  <Input
                    type="number"
                    value={f.fixedMode === 'amount' ? f.fixedAmount : Number.isFinite(rate) ? String(Math.round(amount * rate)) : ''}
                    onChange={(e) => setF({ ...f, fixedMode: 'amount', fixedAmount: e.target.value })}
                  />
                </Field>
                <Field label={t('Rate ({to} per {from})', { to: f.payCurrency, from: f.salaryCurrency })}>
                  <Input
                    type="number"
                    step="0.0001"
                    value={f.fixedMode === 'rate' ? f.fixedRate : Number.isFinite(rate) ? rate.toFixed(4) : ''}
                    onChange={(e) => setF({ ...f, fixedMode: 'rate', fixedRate: e.target.value })}
                  />
                </Field>
              </div>
            )}
          </div>
        )}
        <Toggle checked={f.overtimeEligible} onChange={(v) => setF({ ...f, overtimeEligible: v })} label={t('Overtime is paid')} description={t("Hours above the month's required hours are paid at the overtime multiplier (Settings → Payroll).")} />
        <Field label={t('Note (optional)')}>
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={500} placeholder={t('e.g. Annual raise')} />
        </Field>
      </div>
    </Modal>
  );
}

/** "EG38 0019 0005 …" — IBANs are read and checked in groups of four. */
const groups = (v: string) => v.replace(/(.{4})/g, '$1 ').trim();

function BankCard({ employeeId, employeeName }: { employeeId: string; employeeName: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['bank', employeeId],
    queryFn: () => api.get<{ bank: BankAccountDto | null }>(`/payroll/employees/${employeeId}/bank`),
  });
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.del(`/payroll/employees/${employeeId}/bank`),
    onSuccess: () => {
      setRemoving(false);
      qc.invalidateQueries({ queryKey: ['bank', employeeId] });
    },
  });
  const bank = q.data?.bank ?? null;
  return (
    <Card
      title={t('Bank details')}
      actions={
        !q.isLoading && (
          <div className="flex gap-2">
            {bank && <Button size="sm" variant="ghost" icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => setRemoving(true)} aria-label={t('Remove bank details')} />}
            <Button size="sm" variant={bank ? 'secondary' : 'primary'} icon={bank ? <Pencil className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} onClick={() => setEditing(true)}>
              {bank ? t('Edit') : t('Add bank details')}
            </Button>
          </div>
        )
      }
    >
      <ErrorBanner error={q.error} />
      {q.isLoading ? (
        <Spinner />
      ) : bank ? (
        <div className="flex items-start gap-3">
          <span className="rounded-lg bg-slate-100 p-2 text-slate-600">
            <Landmark className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1 space-y-3">
            <DescList
              items={[
                { label: t('Name on the bank account'), value: bank.accountHolder },
                { label: t('Bank'), value: bank.bankName },
                { label: t('Account number'), value: bank.accountNumber ? <span className="font-mono" dir="ltr">{bank.accountNumber}</span> : null },
                { label: t('IBAN'), value: bank.iban ? <span className="font-mono" dir="ltr">{groups(bank.iban)}</span> : null },
                ...(bank.swift ? [{ label: t('SWIFT / BIC'), value: <span className="font-mono" dir="ltr">{bank.swift}</span> }] : []),
              ]}
            />
            <p className="text-xs text-slate-500">
              {t('Updated {date} by {name}.', { date: fmtDate(bank.updatedAt.slice(0, 10)), name: bank.updatedBy ?? '—' })}{' '}
              {t('Only administrators with payroll access see these details. Payslips show only the last 4 characters.')}
            </p>
          </div>
        </div>
      ) : (
        <EmptyState title={t('No bank details')} description={t("Add {name}'s bank account to include it in the bank transfer list on the Payroll page.", { name: employeeName })} />
      )}
      {editing && <BankModal employeeId={employeeId} current={bank} onClose={() => setEditing(false)} />}
      <ConfirmDialog
        open={removing}
        onClose={() => setRemoving(false)}
        onConfirm={() => remove.mutate()}
        title={t('Remove bank details?')}
        message={bank ? `${bank.accountHolder} · ${bank.bankName}` : ''}
        confirmLabel={t('Remove')}
        danger
        loading={remove.isPending}
        error={remove.error}
      />
    </Card>
  );
}

function BankModal({ employeeId, current, onClose }: { employeeId: string; current: BankAccountDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    accountHolder: current?.accountHolder ?? '',
    bankName: current?.bankName ?? '',
    accountNumber: current?.accountNumber ?? '',
    iban: current?.iban ? groups(current.iban) : '',
    swift: current?.swift ?? '',
  });
  const save = useMutation({
    mutationFn: () => api.put(`/payroll/employees/${employeeId}/bank`, f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank', employeeId] });
      onClose();
    },
  });
  const errs = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal
      open
      onClose={onClose}
      title={current ? t('Edit bank details') : t('Add bank details')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('Cancel')}</Button>
          <Button loading={save.isPending} disabled={!f.accountHolder.trim() || !f.bankName.trim() || !(f.accountNumber.trim() || f.iban.trim())} onClick={() => save.mutate()}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <ErrorBanner error={Object.keys(errs).length ? null : save.error} />
      <div className="space-y-4">
        <Field label={t('Name on the bank account')} required error={errs.accountHolder} hint={t('Exactly as the bank writes it — transfers with a different name can be rejected.')}>
          <Input value={f.accountHolder} onChange={set('accountHolder')} maxLength={140} autoComplete="off" />
        </Field>
        <Field label={t('Bank name')} required error={errs.bankName} hint={t('e.g. {code}', { code: 'CIB, Banque Misr, National Bank of Egypt' })}>
          <Input value={f.bankName} onChange={set('bankName')} maxLength={140} autoComplete="off" />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('Account number')} error={errs.accountNumber}>
            <Input value={f.accountNumber} onChange={set('accountNumber')} maxLength={40} dir="ltr" className="font-mono" autoComplete="off" inputMode="numeric" />
          </Field>
          <Field label={t('SWIFT / BIC (optional)')} error={errs.swift} hint={t('Needed for transfers from outside Egypt.')}>
            <Input value={f.swift} onChange={set('swift')} maxLength={11} dir="ltr" className="font-mono uppercase" autoComplete="off" />
          </Field>
        </div>
        <Field label={t('IBAN')} error={errs.iban} hint={t('An Egyptian IBAN starts with EG and has 29 characters. Spaces are fine — the check digits are verified.')}>
          <Input value={f.iban} onChange={set('iban')} maxLength={42} dir="ltr" className="font-mono uppercase" autoComplete="off" />
        </Field>
        <p className="text-xs text-slate-500">{t('Enter the account number, the IBAN, or both.')}</p>
      </div>
    </Modal>
  );
}
